import { applyHardCodedRules, buildColumnStatusMaps } from '@/utilities/hard-coded-board-rules';
import { getInvoiceExcludeStatusIds, buildExcludeString } from '@/utilities/board-status-filter';

/**
 * Board Configuration for different board types
 * Defines column groupings, status mappings, and filters
 */
export interface BoardColumnConfig {
  id: string;
  title: string;
  subTitle?: string;
  borderClass: string;
  statusIds: number[]; // Project status IDs that belong in this column (using intProjectStatusID)
  defaultStatusId?: number; // Default status ID to assign when cards are dropped into this column
  defaultStatusName?: string; // Default status name (for display and API calls)
  order: number;
}

export interface BoardConfig {
  boardType: string;
  title: string;
  columns: BoardColumnConfig[];
  baseFilter: any; // The baseline filter that will always be applied
  cardFields: {
    primary: string;
    title: string;
    customer: string;
    location?: string;
    amount?: string;
    assignee?: string;
    category?: string;
    priority?: string;
    due_date?: string;
    status?: string;
  };
}

/**
 * Jobs/Projects Board Configuration
 * Uses the same API as dashboard with specific filtering and grouping
 * 
 * HARD-CODED STATUS IDs (DO NOT CHANGE):
 * - Scheduled/Dispatch: Always uses status ID 27
 * - In Progress: Always uses status ID 19
 * - Waiting/On Hold: Always uses status ID 15
 * - Completed: Always uses status ID 7
 */
export const JOBS_BOARD_CONFIG: BoardConfig = {
  boardType: 'jobs',
  title: 'Jobs & Projects Board',
  baseFilter: {
    // This will always exclude these project status IDs (equivalent to exclude.projectStatusId=9,13)
    'exclude.projectStatusId': '9,13',
    maximumRows: 200,
    page: 1,
    id: 0
  },
  columns: [
    {
      id: 'new',
      title: 'New',
      subTitle: 'New service calls and work orders',
      borderClass: 'border-top-orange',
      statusIds: [], // Will be configured dynamically through UI
      defaultStatusName: 'New', // Hard-coded default
      // No defaultStatusId - will prompt user to select when jobs are dropped here
      order: 1
    },
    {
      id: 'scheduled_dispatched',
      title: 'Scheduled/Dispatch',
      subTitle: 'Jobs that are scheduled or currently dispatched',
      borderClass: 'border-top-blue',
      statusIds: [], // Will be configured dynamically through UI
      defaultStatusName: 'Scheduled', // Hard-coded default
      defaultStatusId: 27, // Hard-coded to always be status ID 27
      order: 2
    },
    {
      id: 'in_progress',
      title: 'In Progress',
      subTitle: 'Active work being performed',
      borderClass: 'border-top-yellow',
      statusIds: [], // Will be configured dynamically through UI
      defaultStatusName: 'In Progress', // Hard-coded default
      defaultStatusId: 19, // Hard-coded to always be status ID 19
      order: 3
    },
    {
      id: 'waiting_on_hold',
      title: 'Waiting/On Hold',
      subTitle: 'Jobs paused pending action',
      borderClass: 'border-top-grey',
      statusIds: [], // Will be configured dynamically through UI
      defaultStatusName: 'Wait', // Hard-coded default
      defaultStatusId: 15, // Hard-coded to always be status ID 15
      order: 4
    },
    {
      id: 'completed',
      title: 'Completed',
      subTitle: 'Finished jobs ready for invoicing',
      borderClass: 'border-top-green',
      statusIds: [], // Will be configured dynamically through UI
      defaultStatusName: 'Completed', // Hard-coded default
      defaultStatusId: 7, // Hard-coded to always be status ID 7
      order: 5
    }
  ],
  cardFields: {
    primary: 'projectRefNumber',
    title: 'projectName',
    customer: 'businessName',
    location: 'location',
    amount: 'total',
    assignee: 'assignedTechnician',
    category: 'type',
    priority: 'priority',
    due_date: 'dueDate',
    status: 'status'
  }
};

/**
 * Initialize board configuration with status IDs from the API
 * This function should be called when the board loads to populate statusIds arrays
 * 
 * @param boardConfig - The board configuration to update
 * @param statuses - Array of all available project statuses from API
 * @returns Updated board configuration with populated statusIds
 */
export function initializeBoardWithStatuses(boardConfig: BoardConfig, statuses: Array<{ id: number; status?: string; name?: string; }>): BoardConfig {
  // Build column status mappings using hard-coded rules
  const columnStatusMaps = buildColumnStatusMaps(statuses);

  // Create a copy of the board config to avoid mutating the original
  const updatedConfig = JSON.parse(JSON.stringify(boardConfig));

  // Update each column with its status IDs
  updatedConfig.columns.forEach((column: BoardColumnConfig) => {
    if (columnStatusMaps[column.id]) {
      column.statusIds = columnStatusMaps[column.id];
    }
  });

  return updatedConfig;
}

/**
 * Get the column ID for a given project based on its status ID and hard-coded rules
 * Uses hard-coded rules first, then falls back to user configuration
 * Returns null if the item should be excluded from the board
 */
export function getColumnForProject(item: any, boardConfig: BoardConfig): string | null {
  const projectStatusId = item.intProjectStatusID;
  const projectStatusName = item.status;

  // STEP 1: Apply hard-coded rules first (these cannot be overridden)
  const hardCodedColumn = applyHardCodedRules(projectStatusId, projectStatusName);
  if (hardCodedColumn !== undefined) {
    return hardCodedColumn; // This could be a column ID or null (for exclusion)
  }

  // STEP 2: Check user-configured status IDs
  if (typeof projectStatusId !== 'undefined' && projectStatusId !== null) {
    for (const column of boardConfig.columns) {
      if (column.statusIds && column.statusIds.length > 0 && column.statusIds.includes(projectStatusId)) {
        return column.id;
      }
    }
  }

  // STEP 3: Default fallback - everything else goes to "in_progress"
  return 'in_progress';
}

/**
 * Get the default status information for a column when cards are dropped into it
 * Returns the status name and ID that should be assigned to jobs dropped into this column
 */
export function getDefaultStatusForColumn(columnId: string, boardConfig: BoardConfig): { statusName: string; statusId?: number } {
  const column = boardConfig.columns.find(col => col.id === columnId);
  if (!column) {
    return { statusName: 'Unknown' };
  }

  return {
    statusName: column.defaultStatusName || column.title,
    statusId: column.defaultStatusId
  };
}

/**
 * Get all status IDs that should NOT appear in any column
 * These will be filtered out from the results
 */
export function getExcludedStatuses(boardConfig: BoardConfig): number[] {
  // For now, return empty array since we're using explicit status ID assignment
  // This could be extended to include additional statuses to exclude based on business rules
  return [];
}

/**
 * Apply the base filter and any additional filters
 * Enhanced to include dynamic invoice status exclusions
 */
export async function buildBoardFilter(
  boardConfig: BoardConfig,
  additionalFilters?: any,
  globalService?: any,
  MODULE?: any,
  forceRefresh: boolean = false
): Promise<any> {
  const filter = { ...boardConfig.baseFilter };

  // Add invoice status exclusions if globalService and MODULE are provided
  if (globalService && MODULE) {
    try {
      const invoiceStatusIds = await getInvoiceExcludeStatusIds(globalService, MODULE, forceRefresh);

      if (invoiceStatusIds.length > 0) {
        // Build enhanced exclude string combining existing excludes with invoice status IDs
        const enhancedExclude = buildExcludeString(filter['exclude.projectStatusId'], invoiceStatusIds);
        filter['exclude.projectStatusId'] = enhancedExclude;
      }
    } catch (error) {
      // If there's an error getting invoice excludes, continue with existing filter
      // Error getting invoice status excludes - continuing with base filter
    }
  }

  if (additionalFilters) {
    Object.assign(filter, additionalFilters);
  }

  return filter;
}

/**
 * Legacy synchronous version for backward compatibility
 * Use buildBoardFilter for new implementations
 */
export function buildBoardFilterSync(boardConfig: BoardConfig, additionalFilters?: any): any {
  const filter = { ...boardConfig.baseFilter };
  
  if (additionalFilters) {
    Object.assign(filter, additionalFilters);
  }
  
  return filter;
}

/**
 * Group API results by board columns
 */
export function groupResultsByColumns(results: any[], boardConfig: BoardConfig): any {
  const groupedResults: { [columnId: string]: any[] } = {};
  
  // Initialize empty arrays for each column
  boardConfig.columns.forEach(column => {
    groupedResults[column.id] = [];
  });
  
  // Group results by their status
  results.forEach(item => {
    const columnId = getColumnForProject(item, boardConfig);
    if (groupedResults[columnId]) {
      groupedResults[columnId].push(item);
    }
  });
  
  return groupedResults;
}

// Export the configurations
export const BOARD_CONFIGS = {
  jobs: JOBS_BOARD_CONFIG
};

export default BOARD_CONFIGS;
